"use client";

import CheckboxInput from "@/components/UI/Input/Checkbox/Checkbox";
import SelectInput from "@/components/UI/Input/SelectInput/SelectInput";
import TextInput from "@/components/UI/Input/TextInput/TextInput";
import * as Form from "@radix-ui/react-form";
import { InputHTMLAttributes, forwardRef, Ref } from "react";
import styles from "./FormField.module.scss";
import clsx from "clsx";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
	name: string;
	label?: string;
	placeholder?: string;
	helperText?: string;
	errorMessage?: string;
	options?: {
		text?: string;
		value?: string | number;
		isLabel?: boolean;
	}[];
	prefix?: string;
	customClass?: string;
}

const FormField = forwardRef<HTMLInputElement, Props>((props, ref) => {
	const {
		name,
		label = "",
		value,
		placeholder = label,
		helperText,
		errorMessage,
		type,
		options,
		prefix,
		onChange,
		customClass,
	} = props;

	const inputHTML = () => {
		switch (type) {
			case "checkbox": {
				return (
					<CheckboxInput
						{...props}
						checked={value ? true : false}
						label={label}
					/>
				);
			}
			case "select": {
				return (
					<SelectInput
						ref={ref as Ref<HTMLButtonElement>}
						label={label}
						placeHolder={placeholder}
						options={options}
						prefix={prefix}
						errorMessage={errorMessage}
						onChange={onChange}
						defaultValue={value?.toString()}
					/>
				);
			}
			default: {
				return (
					<TextInput
						{...props}
						ref={ref}
						className={styles.input}
						value={value}
						errorMessage={errorMessage}
						placeholder={placeholder}
					/>
				);
			}
		}
	};

	return (
		<Form.FormField
			name={name}
			className={clsx([styles.formField, customClass])}
		>
			{label && type !== "checkbox" && (
				<Form.Label className={styles.label}>
					{label} {props.required && <span className={styles.asterisk}>*</span>}
				</Form.Label>
			)}
			<Form.Control asChild>{inputHTML()}</Form.Control>
			{errorMessage && (
				<Form.Message className={styles.errorMessage}>
					{errorMessage}
				</Form.Message>
			)}
			{helperText && (
				<Form.Message className={styles.helpText}>{helperText}</Form.Message>
			)}
		</Form.FormField>
	);
});

export default FormField;
